import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import FlexContainer from 'core-system/FlexContainer'
import FormattingUtils from 'shared/FormattingUtils'
import { ReimbursementReceiptDetails } from 'redux/reimbursements/reimbursementsTypes'
import { buildBudgetRows, getBudgetsSectionLabel } from '../utils'
import ReimbursementBudgetRow from './ReimbursementBudgetRow'

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1rem 0;
  border-bottom: 1px solid ${(props) => props.theme.palette.grey.grey300};
  gap: 0.5rem;

  &:last-child {
    border-bottom: none;
  }
  @media (max-width: ${(p) => p.theme.breakpoints[0]}) {
    flex-direction: column;
  }
`

const DetailLabel = styled(Text)`
  font-weight: 600;
  width: ${(props) => props.theme.pxToRem(200)};
  flex-shrink: 0;
  @media (max-width: ${(p) => p.theme.breakpoints[0]}) {
    width: auto;
  }
`

const RowsContainer = styled(FlexContainer)`
  flex: 1;
  min-width: 0;
  gap: 0.5rem;
  @media (max-width: ${(p) => p.theme.breakpoints[0]}) {
    gap: 1.25rem;
  }
`

export interface ReimbursementBudgetsProps {
  receipt: ReimbursementReceiptDetails
  budgetAllocations: Record<string, string>
  onBudgetAllocationsChange: (next: Record<string, string>) => void
  remainingReimbursementBalance: number | null
}

const ReimbursementBudgets = React.memo(
  ({
    receipt,
    budgetAllocations,
    onBudgetAllocationsChange,
    remainingReimbursementBalance,
  }: ReimbursementBudgetsProps) => {
    const rows = buildBudgetRows(receipt, budgetAllocations)
    const label = getBudgetsSectionLabel(receipt.status)

    return (
      <DetailRow>
        <DetailLabel variant='body1'>{label}</DetailLabel>
        <RowsContainer flexDirection='column'>
          {rows.map((row) => (
            <ReimbursementBudgetRow
              key={row.key}
              row={row}
              receipt={receipt}
              budgetAllocations={budgetAllocations}
              onBudgetAllocationsChange={onBudgetAllocationsChange}
            />
          ))}
          {remainingReimbursementBalance !== null &&
            receipt.status === 'CREATED' && (
              <Text
                variant='body1'
                style={{ marginTop: '0.75rem', fontWeight: 600 }}
              >
                Remaining reimbursement balance:{' '}
                {FormattingUtils.formatDollar(remainingReimbursementBalance)}
              </Text>
            )}
        </RowsContainer>
      </DetailRow>
    )
  }
)

ReimbursementBudgets.displayName = 'ReimbursementBudgets'

export default ReimbursementBudgets
